import { CircularProgress, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { CheckCircle } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, store, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { motions, StatusIcon, Tabs } from '../../global';
import { Details } from './Details';
import { Overview } from './Overview';
import { Plan } from './Plan';
import { Projects } from './Projects';

const useAssetDrawerClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    spinner: {
        alignSelf: 'center',
        justifySelf: 'center',
        marginTop: 100,
    },
    tabs: {
        padding: '0 20px',
        boxShadow: '0 15px 20px -15px rgba(27, 31, 53, 0.2)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: 0,
        padding: '30px 30px 20px 30px',
        overflow: 'auto',
    },
});

/**
 * Asset drawer component.
 *
 * @param props.open reference to set the drawer open function
 */
export const AssetDetails = (props: { code: string }) => {
    const classes = useAssetDrawerClasses();
    const dispatch = useTypedDispatch();
    const { t } = usePrefixTranslation('asset', { asset: 'drawer.AssetDrawer' });
    const { asset, plan } = useTypedSelector(state => ({ asset: state.asset, plan: state.plan }));
    const fetching = (asset.fetching && asset.status !== 'updating') || (plan.fetching && plan.status !== 'updating');

    React.useEffect(() => {
        (async () => {
            await dispatch(actions.asset.get(props.code));
            const planId = store.getState().asset.asset?.inspection_plan;
            if (planId != undefined) await dispatch(actions.plan.get(planId));
        })();
    }, []);

    if (!fetching && !asset.asset) return <span>{'#asset not in store'}</span>;

    return (
        <div className={classes.root}>
            <Header />
            <AnimatePresence exitBeforeEnter>
                <motion.div key={+fetching} className={classes.container} {...motions.presence({})}>
                    {fetching ? (
                        <CircularProgress className={classes.spinner} size={60} />
                    ) : (
                        <Tabs
                            tabsProps={{ className: classes.tabs }}
                            paneProps={{ className: classes.content }}
                            labels={[t('overview'), t('details'), t('plan'), t('projects')]}
                        >
                            <Overview />
                            <Details />
                            <Plan />
                            <Projects />
                        </Tabs>
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

const useHeaderClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 30px 10px',
        color: theme.colors.darkBlue[5],
        '& > span:first-child': {
            ...theme.typography.content.caption.paragraph,
            textTransform: 'capitalize',
        },
        '& > span:last-child': {
            display: 'flex',
            gap: 10,
            ...theme.typography.headings.h1,
        },
    },
});

/**
 * Component that displays the asset drawer header.
 */
const Header = () => {
    const classes = useHeaderClasses();
    const { asset } = useTypedSelector(state => ({ asset: state.asset }));
    const fetching = asset.fetching && asset.status !== 'updating';

    if (fetching || !asset.asset)
        return (
            <div className={classes.root}>
                <Skeleton width='25%' />
                <Skeleton width='50%' />
            </div>
        );
    return (
        <div className={classes.root}>
            <span>{`${asset.asset.code} | ${asset.asset.type?.name ?? '#no type defined'}`}</span>
            <span>
                {asset.asset.name}
                {(asset.asset.psm || true) && <StatusIcon type='info' icon={<CheckCircle />} />}
            </span>
        </div>
    );
};
